import React, {
  useContext, useCallback, useMemo, useState
} from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@glu/theming';
import { Input } from '@glu/form-components';
import { FormContext } from '@glu/validation-react';
import locale from '@glu/locale';
import Popover from '../Popover/Popover';

import styles from './FontFamily.styles';

const FontFamily = ({
  classes, name, labelText, urlName, placeholder, tooltip
}) => {
  const { data, onChange } = useContext(FormContext);
  const fontFamily = data[name] || '';
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');

  const urls = useMemo(() => data[urlName] || [], [urlName, data]);

  const onClick = useCallback((e) => {
    e.preventDefault();
    if (fontFamily) {
      const urlIndex = urls.findIndex(u => u === url);
      if (urlIndex >= 0) {
        /** removes only 1 URL in case there are duplicates
         * Duplicates are removed on download
         */
        onChange(urlName, urls.filter((_v, i) => i !== urlIndex));
      }
      setUrl('');
      onChange(name, '');
    } else {
      const isUrl = url.startsWith('http');
      if (isUrl) {
        // Valid URL - pull the font in
        fetch(url).then(resp => resp.text()).then(val => {
          const regex = /font-family: '(.*)';$/m;
          const newFamily = regex.exec(val)?.[1];
          if (newFamily) {
            setError('');
            onChange(urlName, urls.concat(url));
            onChange(name, newFamily);
          } else {
            setError(locale.get('branding.error.badUrl'));
          }
        }).catch(() => setError(locale.get('branding.error.invalidUrl')));
      } else {
        // Not a URL - use entered text
        setError('');
        onChange(name, url);
      }
    }
  }, [name, url, urls, urlName, fontFamily, onChange]);

  return (
    <div className={classes.root}>
      <Input
        name={name}
        labelText={labelText}
        value={fontFamily || url}
        onChange={e => setUrl(e.target.value)}
        placeholder={placeholder}
        disabled={!!fontFamily}
        error={error}
        disableInputAPI
      />
      <button type="button" className={classes.button} onClick={onClick}>
        {fontFamily ? locale.get('branding.remove') : locale.get('branding.load')}
      </button>
      <Popover>
        {tooltip}
      </Popover>
    </div>
  );
};

FontFamily.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  name: PropTypes.string.isRequired,
  labelText: PropTypes.string.isRequired,
  urlName: PropTypes.string,
  placeholder: PropTypes.string,
  tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
};

FontFamily.defaultProps = {
  urlName: '__fontFamilyUrls__',
  placeholder: '',
  tooltip: ''
};

export default withStyles(styles)(FontFamily);
